/* System defined core library */
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import * as moment from 'moment';

/* User defined library/components */
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';
import { TimerService } from '../../../shared/services/timer.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { MetricData, MetricDetails } from '../../../shared/models/scheduling/metrics.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { SiteSlot } from '../../../shared/models/scheduling/siteSlot.model';
import { Slot } from '../../../shared/models/scheduling/slot.model';
import { Test } from '../../../shared/models/scheduling/test.model';

/* User defined enums/constants */
import { ComponentType, Fee, MetricType } from '../../../shared/enums/scheduling.enum';
import { TestCenterSelecterComponent } from '../../shared/components/testcenter-selecter/testcenter-selecter.component';


@Component({
  selector: 'reschedule-testcenter',
  templateUrl: './reschedule-testcenter.component.html',
  styleUrls: ['./reschedule-testcenter.component.scss']
})
export class RescheduleTestCenterComponent implements OnInit {
  @HostListener('window:popstate', ['$event'])
  @ViewChild('refreshavailabilitybtn') refreshBtn: ElementRef;
  @ViewChild(TestCenterSelecterComponent) testCenterSelecterComponentInstance : TestCenterSelecterComponent;

  onPopState(event: any) {
    this.clearDataOnGoingBack();
  }

  /* User defined private/public variables */
  public booking: Booking;
  public client: Client;
  public firstTimeLocationSearch: boolean[] = [];
  public holdSlotObj: Hold;
  public isSlotSelected: boolean = false;
  public metricsDetail: MetricDetails[];
  public prevSelectedSlot: Slot;
  public prevSelectedHoldObj: any = [];
  public prevSelectedHoldObjArr: any = [];
  public prevSelectionsArr: SiteSlot[] = [];
  public prevSites: any = [];
  public prevSlotSelections: any = [];
  public program: Program;
  public region: any;
  public scheduledSlot: AppointmentSlot;
  public selectedAppointments: AppointmentDetail[] = [];
  public selectedIndex: number = 0;
  public selectedSite: SiteSlot;
  public selectedStartDate: any;
  public siteSlots: SiteSlot[];
  public hasConflictErrorOccured: boolean;

  /* Service subscriptions */
  booking$: Subscription;
  client$: Subscription;
  firstTimeLocationSearch$: Subscription;
  hold$: Subscription;
  metricsDetail$: Subscription;
  prevSites$: Subscription; // previous searched sites
  prevSelectedSlot$: Subscription;
  prevSelectedHoldObj$: Subscription;
  prevSelectionsArr$: Subscription; // selected sites
  prevSlotSelections$: Subscription; // selected slots
  program$: Subscription;
  region$: Subscription;
  scheduledSlot$: Subscription;
  selectedAppointments$: Subscription;
  selectedSite$: Subscription;
  selectedStartDate$: Subscription;
  siteSlots$: Subscription;
  translateLanguage$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private orchestrationApiService: OrchestrationApiService,
    private routingService: RoutingService,
    private selectedExamDetailService: SelectedExamDetailService,
    private schedulingApiService: SchedulingApiService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private timerService: TimerService,
    private cdRef: ChangeDetectorRef
  ) {
    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    this.client$ = this.selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.firstTimeLocationSearch$ = selectedExamDetailService.firstTimeLocationSearch.subscribe(
      value => {
        const boolVal: boolean[] = [];
        boolVal.push(false);
        this.firstTimeLocationSearch = value == null ? boolVal : value;
      });

    this.hold$ = selectedExamDetailService.hold.subscribe(
      hold => {
        this.holdSlotObj = hold;
      });

    this.metricsDetail$ = selectedExamDetailService.metricsDetail.subscribe(
      metricsDetail => {
        this.metricsDetail = metricsDetail;
      });

    this.prevSelectedHoldObj$ = selectedExamDetailService.prevSelectedHoldObj.subscribe(
      prevSelectedHoldObj => {
        this.prevSelectedHoldObj = prevSelectedHoldObj;
        if (this.prevSelectedHoldObj) {
          this.prevSelectedHoldObjArr = prevSelectedHoldObj;
        }
      });

    this.prevSelectedSlot$ = this.selectedExamDetailService.slot.subscribe(
      slot => {
        this.prevSelectedSlot = slot;
      });

    // selected sites until now
    this.prevSelectionsArr$ = this.selectedExamDetailService.prevSelections.subscribe(
      siteSlots => {
        this.prevSelectionsArr = siteSlots || [];
      });

    // selected slots until now
    this.prevSlotSelections$ = this.selectedExamDetailService.prevSlotSelections.subscribe(
      slots => {
        this.prevSlotSelections = slots || [];
      });

    this.prevSites$ = this.selectedExamDetailService.prevSites.subscribe(
      sites => {
        this.prevSites = sites || [];
      });

    this.program$ = this.selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.region$ = selectedExamDetailService.region.subscribe(
      region => {
        this.region = region;
      });

    this.scheduledSlot$ = this.selectedExamDetailService.scheduledSlot.subscribe(
      scheduledSlot => {
        this.scheduledSlot = scheduledSlot;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.selectedSite$ = this.selectedExamDetailService.siteSlot.subscribe(
      siteSlot => {
        this.selectedSite = siteSlot;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.selectedStartDate$ = selectedExamDetailService.selectedStartDate.subscribe(
      selectedStartDate => {
        this.selectedStartDate = selectedStartDate;
      });

    this.siteSlots$ = selectedExamDetailService.prevSiteSlots.subscribe(
      siteSlots => {
        this.siteSlots = siteSlots;
      });

    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.RescheduleTestCenterComponent)) { return; }

    if (this.prevSelectedSlot) {
      this.isSlotSelected = true;
    }

    this.selectedAppointments.forEach(
      appt => {
        if (this.utilityService.isNullOrUndefined(appt.exam.hasPayment)) {
          appt.exam.hasPayment = true;
        }
      });
  }

  /* Move back a page. */
  goToPrevPage() {
    this.clearDataOnGoingBack();
    this.routingService.backwardNavigation(ComponentType.RescheduleTestCenterComponent);
  }

  /* Use to clear details on navigating backward */
  clearDataOnGoingBack() {
    this.selectedExamDetailService.setSiteSlot(null);
    this.selectedExamDetailService.setSlot(null);
    this.selectedExamDetailService.setSiteSlots(null);
    this.selectedExamDetailService.setSelectedAvailableSlots(null);
    this.selectedExamDetailService.setSkipSize(null);

    this.timerService.clearClock();

    if (!this.selectedExamDetailService.isBackToBackRequired && this.selectedAppointments.length > 1) {
      this.selectedAppointments[this.selectedIndex].appointmentSlot = new AppointmentSlot();
      this.selectedAppointments[this.selectedIndex].hold = new Hold();
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
      if (this.prevSelectionsArr.length || this.prevSites.length) {
        this.prevSelectionsArr[this.selectedIndex] = null;
        this.prevSites[this.selectedIndex] = [];
      }
      if (this.prevSlotSelections.length) {
        this.prevSlotSelections[this.selectedIndex] = [];
      }
      this.selectedExamDetailService.setPrevSites(this.prevSites);
      this.selectedExamDetailService.setPrevSlotSelections(this.prevSlotSelections);
      this.selectedExamDetailService.setPrevSelections(this.prevSelectionsArr);
    } else {
      this.prevSites = [];
      this.prevSlotSelections = [];
      this.prevSelectionsArr = null;
      this.selectedExamDetailService.setPrevSites(this.prevSites);
      this.selectedExamDetailService.setPrevSlotSelections(this.prevSlotSelections);
      this.selectedExamDetailService.setPrevSelections(this.prevSelectionsArr);
      this.selectedAppointments.forEach((appointment) => {
        appointment.appointmentSlot = new AppointmentSlot();
        appointment.hold = new Hold();
      });
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
    }
  }

  /* Move to next page */
  goToNextPage() {
    this.holdOrSetDetail();
  }

  // TODO - move these methods to service - KJC
  getPaymentDetails(): void {
    let totalAmount: number = 0;
    let currencyCode: string = '';

    this.selectedAppointments.forEach(
      (appointmentDetail) => {
        appointmentDetail.payments = [];
        this.schedulingApiService.getBookingPaymentFeeDetail(appointmentDetail.id, appointmentDetail.exam.id).subscribe((paymentFee) => {
          paymentFee.forEach(payment => {
            if (payment.type === Fee.RescheduleFee) {
              let testData: Test = this.booking.tests.find((test) => {
                return test.exam.id === appointmentDetail.exam.id;
              });

              const tmpPayment: Payment = new Payment(
                !testData.cnatt ? payment.amount : 0,
                payment.currency,
                Fee.RescheduleFee
              );
              totalAmount += tmpPayment.amount;
              currencyCode = tmpPayment.currency;
              appointmentDetail.payments.push(tmpPayment);
              this.selectedExamDetailService.rescheduleFee = {
                totalAmount: totalAmount,
                currency: currencyCode
              };

              this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;

              this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

              if (!appointmentDetail.exam.hasPayment) {
                this.clearPaymentDetails(appointmentDetail.exam.id);
              }
            }
          });
        });
      });
  }

  // TODO - move these methods to service - KJC
  clearPaymentDetails(examId: any) {
    let totalAmount = 0;
    this.selectedAppointments.forEach(
      (appointmentDetail) => {
        if (appointmentDetail.exam.id === examId) {
          appointmentDetail.payments.forEach((payment) => {
            if (payment) {
              payment.amount = 0;
            } else {
              appointmentDetail.payments.push(new Payment(0));
            }
          });
        }
      });

    this.selectedAppointments.forEach(appt => totalAmount += appt.payments.length ? appt.payments[0].amount : 0);
    this.selectedExamDetailService.rescheduleFee['totalAmount'] = totalAmount;

    this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  // TODO - move these methods to service - KJC
  setSettingsFromFlows(response: any, examId?: string) {
    const notificationResp = response.filter(function (el: any) {
      return el.name === 'Verification';
    });

    this.selectedExamDetailService.showTextReminder = notificationResp[0].data &&
      notificationResp[0].data.length &&
      notificationResp[0].data[0].parameters.smsText === 'True' ? true : false;

    const paymentResp = response.filter(function (el: any) {
      return el.name === 'Payment';
    });

    examId = examId || this.selectedAppointments[this.selectedIndex].exam.id;

    this.selectedAppointments.forEach(
      appt => {
        if (appt.exam.id === examId) {
          appt.exam.hasPayment = paymentResp.length && paymentResp[0].settings.reschedule === 'True';
        }
      });

    const biometricResp = response.filter(function (el: any) {
      return el.name === 'Biometric Consent';
    });

    // Exam Set does not return biometric as we call it
    if (biometricResp.length) {
      this.selectedExamDetailService.isBiometricConsentRequiredReschedule =
        !this.selectedExamDetailService.isBiometricConsentSavedInSchedule && (
          biometricResp[0].settings &&
          biometricResp[0].settings.consent === 'True'
        );
    }

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

    this.selectedAppointments.forEach(
      appt => {
        if (!appt.exam.hasPayment) {
          this.clearPaymentDetails(appt.exam.id);
        }
      });
  }

  // TODO - move these methods to service - KJC
  holdOrSetDetail() {
    this.isSlotSelected = false; // Only need to disable the next button once clicked (No hide)

    if (this.holdSlotObj && this.prevSelectedHoldObj && this.prevSelectedHoldObj.length) {
      const commonHolds = this.prevSelectedHoldObj.filter((holdObj: any) => {
        return holdObj.slot.id === this.holdSlotObj.slot.id;
      });
      if (commonHolds.length) {
        this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleTestCenterComponent);
      } else {
        this.holdSlot(this.holdSlotObj);
        this.getPaymentDetails();
      }
    } else {
      this.holdSlot(this.holdSlotObj);
      this.getPaymentDetails();
    }
  }

  /* disable next button on the basis of local variable value */
  public isDisable(): boolean {
    return !this.isSlotSelected;
  }

  public setSelectedSiteDetails(selectedSiteDetails: any): void {
    this.selectedAppointments = selectedSiteDetails.selectedAppointments;
    this.selectedIndex = selectedSiteDetails.selectedIndex;
    this.holdSlotObj = selectedSiteDetails.holdSlotObj;
    this.isSlotSelected = true;
    this.hasConflictErrorOccured = false;
    this.metricsDetail = selectedSiteDetails.metricsDetail;
  }

  public setSlotsCheck(slotsCheck: any): void {
    this.hasConflictErrorOccured = false;
    this.isSlotSelected = slotsCheck;
  }

  refreshSlotAvailability(): void {
    this.testCenterSelecterComponentInstance.submitSearch();
  }

  // TODO - move these methods to service - KJC
  holdSlot(holdSlot: Hold): void {

    this.firstTimeLocationSearch$ = this.selectedExamDetailService.firstTimeLocationSearch.subscribe(value => {
      const boolVal: boolean[] = [];
      boolVal.push(false);
      this.firstTimeLocationSearch = value == null ? boolVal : value;
    });

    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;

    if (this.selectedExamDetailService.isBackToBackRequired) {
      const requestMultipleHolds = {
        'slots': [{
          'id': holdSlot.slot.id
        }]
      };

      this.schedulingApiService.holdExamsBackToBackRequired(requestMultipleHolds).subscribe((holdsObjects: any) => {
        if (holdsObjects.length > 0) {

          if ((this.metricsDetail == undefined || this.metricsDetail == null || this.metricsDetail[0].searched == null || this.metricsDetail[0].searched == undefined) && this.firstTimeLocationSearch[0] === false) {
            this.setMetricsDetail(MetricType.Searched, null, this.selectedAppointments[0].hold.slot.startDateTime);
          }

          this.setMetricsDetail(MetricType.Selected, this.selectedAppointments[0].appointmentSlot.site.id, this.selectedAppointments[0].hold.slot.startDateTime, this.selectedAppointments[0].exam.id);
          this.setMetricsDetail(MetricType.BestDate, this.selectedAppointments[0].appointmentSlot.site.id);

          const selSlot = this.selectedAppointments[0].hold.slot;
          for (let count: number = 0; count < this.selectedAppointments.length; count++) {
            this.selectedAppointments[count].hold = holdsObjects[count];
            this.selectedAppointments[count].holdId = holdsObjects[count].id;

            if (count === 0) { // only update id of first hold slot because it will be use when candidate goes previous for release hold if already selected slot choose.
              holdSlot.id = holdsObjects[count].id;
              this.selectedExamDetailService.setHold(holdSlot);
              this.prevSelectedHoldObjArr.push(holdSlot);
              this.selectedExamDetailService.setprevSelectedHoldObj(this.prevSelectedHoldObjArr);
            }
          }
          // hack to maintain the slot as it's being replaced in another line
          // so that the slot selected will be shown when the user comes back
          this.selectedAppointments[0].hold.slot = selSlot;

          this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

          // retrieving consent for first exam
          this.orchestrationApiService.getFlows(
            this.client.code,
            this.program.code,
            this.selectedAppointments[this.selectedIndex].exam.code,
            this.scheduledSlot.site.location.address.country
          ).subscribe((resp) => {
            this.setSettingsFromFlows(resp);
          });

          this.orchestrationApiService.getExamSetFlows(
            this.client.code,
            this.program.code,
            this.selectedExamDetailService.examSetId
          ).subscribe((resp: any[]) => {
            let examsOutOfSet = 0;
            this.selectedAppointments.forEach(appointment => {
              if (!resp.find((flow) => { return flow.id === appointment.exam.id })) {
                examsOutOfSet++;
                this.orchestrationApiService.getFlows(
                  this.client.code,
                  this.program.code,
                  appointment.exam.code,
                  this.scheduledSlot.site.location.address.country
                ).subscribe((resp) => {
                  this.setSettingsFromFlows(resp, appointment.exam.id);
                  this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleTestCenterComponent);
                });
              }
            });
            resp.forEach((flow) => {
              this.setSettingsFromFlows(flow.flowSet, flow.id);
              if (!examsOutOfSet) {
                this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleTestCenterComponent);
              }
            });
          });
        } else {
            if(holdsObjects == -2)
            {
                this.hasConflictErrorOccured = true;
                this.cdRef.detectChanges();
                this.isSlotSelected = false;
                this.refreshBtn.nativeElement.focus();
            }
            else {
                this.isSlotSelected = false;
            }
        }
      });
    } else {
      this.schedulingApiService.holdSiteSlot(this.selectedAppointments[this.selectedIndex].hold).subscribe((holdSlotId: any)=> {
        if (holdSlotId > 0) {
          holdSlot.id = holdSlotId.toString();

          if ((this.metricsDetail == undefined || this.metricsDetail == null || this.metricsDetail[this.selectedIndex].searched == null || this.metricsDetail[this.selectedIndex].searched == undefined) && (this.firstTimeLocationSearch != null && this.firstTimeLocationSearch[this.selectedIndex] === false)) {
            this.setMetricsDetail(MetricType.Searched, null, this.selectedAppointments[this.selectedIndex].hold.slot.startDateTime);
          }
          this.setMetricsDetail(MetricType.Selected,
            this.selectedAppointments[0].appointmentSlot.site.id,
            this.selectedAppointments[0].hold.slot.startDateTime,
            this.selectedAppointments[0].exam.id);
          this.setMetricsDetail(MetricType.BestDate,
           this.selectedAppointments[0].appointmentSlot.site.id,
          );

          this.selectedAppointments[this.selectedIndex].hold.id = holdSlotId.toString();
          this.selectedAppointments[this.selectedIndex].holdId = holdSlot.id;
          this.selectedExamDetailService.setHold(holdSlot);
          this.prevSelectedHoldObjArr.push(holdSlot);
          this.selectedExamDetailService.setprevSelectedHoldObj(this.prevSelectedHoldObjArr);
          this.selectedAppointments[this.selectedIndex].appointmentSlot.id = this.holdSlotObj.slot.id;
          this.selectedAppointments[this.selectedIndex].appointmentSlot.href = this.holdSlotObj.slot.uri;
          this.selectedExamDetailService.SetSlotDataSource(this.selectedAppointments[this.selectedIndex].appointmentSlot);
          this.selectedExamDetailService.isRescheduleSlotSelected = true;

          // retrieving consent for first exam
          this.orchestrationApiService.getFlows(
            this.client.code,
            this.program.code,
            this.selectedAppointments[this.selectedIndex].exam.code,
            this.scheduledSlot.site.location.address.country
          ).subscribe((resp) => {
            this.setSettingsFromFlows(resp);
            this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleTestCenterComponent);
          });
        } else {
            if(holdSlotId == -2)
            {
                this.hasConflictErrorOccured = true;
                this.cdRef.detectChanges();
                this.isSlotSelected = false;
                this.refreshBtn.nativeElement.focus();
            }
            else {
                this.isSlotSelected = false;
            }
        }
      });
    }
  }

  /* Set metric details in shared service */
  private setMetricsDetail(metricType: MetricType, siteId?: string, slotdate?: any, examId?: string): void {
    const firstDateAvailable = moment(this.selectedSite.availability[0]).format('YYYY-MM-DDTHH:mm:ss');

    if (this.metricsDetail == null || this.metricsDetail == undefined) {
      this.metricsDetail = [];
      this.metricsDetail.push(new MetricDetails());
    } else if (this.metricsDetail != null && this.selectedIndex > this.metricsDetail.length) {
      this.metricsDetail.push(new MetricDetails());
    }

    if (metricType === MetricType.BestSite && this.region) {
      this.metricsDetail[this.selectedIndex].bestSite = new MetricData(
        MetricType.BestSite,
        { 'latitude': this.region.lat, 'longitude': this.region.long, 'siteId': siteId },
        slotdate
      );
      this.selectedExamDetailService.setMetricsDetail(this.metricsDetail);
    } else if (metricType === MetricType.Selected && this.region) {
      this.metricsDetail[this.selectedIndex].selected = new MetricData(
        MetricType.Selected,
        { 'latitude': this.region.lat, 'longitude': this.region.long, 'siteId': siteId },
        slotdate
      );
      this.metricsDetail[this.selectedIndex].examId = examId;
      this.selectedExamDetailService.setMetricsDetail(this.metricsDetail);
    } else if (metricType === MetricType.BestDate) {
      this.metricsDetail[this.selectedIndex].bestDate = new MetricData(
        MetricType.BestDate,
        {
          'searchedSiteId': siteId,
          'offeredSiteId': siteId,
          'searchedDate': this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate.jsdate), '-')
        },
        this.utilityService.getFormatToMMDDYYYY(new Date(firstDateAvailable), '-')
      );
      this.selectedExamDetailService.setMetricsDetail(this.metricsDetail);
    } else if (metricType === MetricType.Searched && this.region) {
      this.metricsDetail[this.selectedIndex].searched = new MetricData(
        MetricType.Searched,
        { 'latitude': this.region.lat, 'longitude': this.region.long },
        this.utilityService.getFormatToMMDDYYYY(new Date(slotdate), '-')
      );

      this.selectedExamDetailService.setMetricsDetail(this.metricsDetail);

      if (!this.firstTimeLocationSearch[this.selectedIndex]) {
        this.firstTimeLocationSearch[this.selectedIndex] = true;
        this.selectedExamDetailService.setFirstTimeLocationSearch(this.firstTimeLocationSearch);

      } else {
        this.firstTimeLocationSearch[this.selectedIndex] = false;
        this.selectedExamDetailService.setFirstTimeLocationSearch(this.firstTimeLocationSearch);
      }
    }
  }
}
